<template>
  <div v-if="content && hub">
    <ClientOnly>
      <AcceptTermsModal v-if="displayTermsModal" :hub="hub" />
    </ClientOnly>

    <HubHeader :breadcrumbs-items="breadcrumbsItems">
      <SendContentModal
        v-if="!hub.isContentSendingDeactivated"
        :content="content"
        :hub-slug="hubSlug"
        :content-slug="contentSlug"
      />
    </HubHeader>

    <div class="flex items-center mt-5">
      <ContentTypeIcon :content-type="content.type" />
      <span class="text-on-background">
        {{ ContentTypeLabelMap[content.type] }}
      </span>
    </div>

    <div class="flex justify-between flex-wrap gap-y-4 gap-x-20">
      <div>
        <h1 class="font-bold text-3xl text-on-background">
          {{ content.title }}
        </h1>
        <div class="text-on-background-variant">
          {{ content.description }}
        </div>
      </div>
      <div class="w-full md:w-fit">
        <CompleteContentButton
          v-if="showLaunchHubOptions"
          :content="content"
          :hub-slug="hubSlug"
          @complete="redirectOnComplete"
        />
      </div>
    </div>
    <div class="mt-5">
      <update-personalization-settings-banner
        v-if="isSomeContentCustomizable"
      />
      <LaunchContentNavigation
        v-if="showLaunchHubOptions"
        class="mt-5"
        :hub-slug="hubSlug"
        :content="content"
      />
      <ResourceArea
        v-if="content.type === ContentType.RESOURCE && content.resource"
        :resource="content.resource"
        :hub-slug="hubSlug"
        :content-slug="contentSlug"
      />
      <ScormCourseArea
        v-else-if="
          content.type === ContentType.SCORM_COURSE && content.scormCourse
        "
        :content="content"
        :scorm-course="content.scormCourse"
      />
      <SocialsArea
        v-else-if="content.type === ContentType.SOCIALS"
        :socials="filteredSocials"
        :hub="hub"
        :content="content"
        :display-post-later-and-edit="hub.displayPostLaterAndEdit"
      />
      <FilesArea
        v-else-if="content.type === ContentType.FILES"
        :content="content"
        :hub="hub"
      />
    </div>

    <LaunchHubCompletionModal
      v-if="isLaunchHubCompletionModalOpened"
      :hub="hub"
    />
  </div>
</template>

<script setup lang="ts">
import { useRoute, useAsyncData, useRouter } from 'nuxt/app';
import { useI18n } from 'vue-i18n';
import { ContentTypeIcon } from '~/components/content-type-icon';
import {
  apiGetContentsList,
  apiGetSingleContent,
  ContentType,
  useContentTypeLabelMap,
} from '~/api/contents';
import { computed, useOrganizationDomain } from '#imports';
import { ResourceArea } from './_components/resource-area';
import { ScormCourseArea } from './_components/scorm-course-area';
import { SocialsArea } from './_components/socials-area';
import { FilesArea } from './_components/files-area';
import { SendContentModal } from './_components/send-content-modal';
import { AcceptTermsModal } from '~/pages/_components/hub-details/_components/accept-terms-modal';
import { useAcceptedHubsTerms } from '~/pages/_components/hub-details/_composables/use-accepted-hubs-terms';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import HubHeader from '~/pages/_components/hub-header/hub-header.vue';
import { handleApiError } from '~/api';
import { UpdatePersonalizationSettingsBanner } from '~/pages/_components/content-details/_components/update-personalization-settings-banner';
import { LaunchContentNavigation } from '~/pages/_components/content-details/_components/launch-content-navigation';
import { CompleteContentButton } from '~/pages/_components/content-details/_components/complete-content-button';
import { createContentLaunchMetric, LaunchMetricType } from '~/api/metrics';
import { useAccountStore } from '~/store/account';
import { AccountType } from '~/api/accounts';
import { useAuthUserStore } from '~/store/auth-user';
import { useHubStore } from '~/store/hub';
import { LaunchHubCompletionModal } from './_components/launch-hub-completion-modal';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const hubSlug = computed<string>(() => route.params.hubslug as string);
const contentSlug = computed<string>(() => route.params.contentslug as string);

const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const { currentAccount } = storeToRefs(useAccountStore());
const { authUser } = storeToRefs(useAuthUserStore());
const { hub } = storeToRefs(useHubStore());

const ContentTypeLabelMap = useContentTypeLabelMap();

const {
  data: content,
  error: contentError,
  refresh,
} = await useAsyncData(`content-${hubSlug.value}-${contentSlug.value}`, () => {
  return apiGetSingleContent(hubSlug.value, contentSlug.value, {
    includeNextPrevSlugs: true,
  });
});

if (contentError.value) {
  await handleApiError(contentError.value);
}

if (
  hub.value?.isLaunchEnabled &&
  currentAccount.value?.type === AccountType.STANDARD &&
  !content.value?.launchData?.openedAt
) {
  await createContentLaunchMetric(
    hub.value.slug,
    content.value.slug,
    LaunchMetricType.OPENED,
  );
  if (content.value?.type === ContentType.RESOURCE) {
    await createContentLaunchMetric(
      hub.value.slug,
      content.value.slug,
      LaunchMetricType.COMPLETED,
    );
  }
  await refresh();
}

useHead({
  title: content.value?.title || organization.value?.name,
});

const { areHubTermsAccepted } = useAcceptedHubsTerms();

const isLaunchHubCompletionModalOpened = ref(false);

const redirectOnComplete = () =>
  useLoader({
    action: async () => {
      const nextNotCompletedContent = await apiGetContentsList(hubSlug.value, {
        page: 1,
        perPage: 1,
        launchMetricTypes: [
          LaunchMetricType.NOT_STARTED,
          LaunchMetricType.OPENED,
        ],
        sortDir: 'asc',
      });
      if (nextNotCompletedContent.totalCount) {
        router.push(
          `/${hubSlug.value}/${nextNotCompletedContent.nodes[0].slug}`,
        );
      } else {
        isLaunchHubCompletionModalOpened.value = true;
      }
    },
  });

const showLaunchHubOptions = computed(
  () =>
    currentAccount.value?.type === AccountType.STANDARD &&
    hub.value?.isLaunchEnabled,
);

const displayTermsModal = computed(() => {
  return (
    process.client &&
    hub.value.displayTerms &&
    !areHubTermsAccepted(hub.value.id)
  );
});

const organizationDomain = useOrganizationDomain();

/* Temporary exclusion of twitter from social posts until customer's decision to use paid API */
const filteredSocials = computed(() => {
  return content.value.socials.filter((social) => social.type !== 'twitter');
});

const isSomeContentCustomizable = computed(() =>
  content.value?.socials?.some((social) => social.isCustomizable),
);

const breadcrumbsItems =
  organizationDomain !== null || !organization.value
    ? [
        { label: t('common.dashboard'), to: '/' },
        { label: hub.value.title, to: `/${hub.value.slug}` },
        {
          label: content.value.title,
          to: `/${hub.value.slug}/${content.value.slug}`,
        },
      ]
    : [
        { label: t('common.dashboard'), to: '/' },
        {
          label: organization.value.name,
          to: `/org/${organization.value.slug}`,
        },
        {
          label: hub.value.title,
          to: `/org/${organization.value.slug}/${hub.value.slug}`,
        },
        {
          label: content.value.title,
          to: `/org/${organization.value.slug}/${hub.value.slug}/${content.value.slug}`,
        },
      ];
</script>
